import { faInfoCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';

const ServiceGroupEditModal = ({
  showModal,
  data,
  handleClose,
  handleSubmit,
  refreshData,
  error
}) => {
  const initialState = {
    name: '',
    daysToAutoClose: 1,
    actionOnCompletedReply: 'REOPEN',
    actionOnClosedReply: 'CREATE_NEW_TICKET',
    inactive: ''
  };
  const [body, setBody] = useState(initialState);
  const [associatedAgents, setAssociatedAgents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [validated, setValidated] = useState(false);
  const api = useApi();
  const form = useRef(null);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  useEffect(() => {
    if (Object.keys(data).length !== 0) {
      setBody({
        ...data,
        actionOnCompletedReply:
          data.actionOnCompletedReply?.action ||
          data.actionOnCompletedReply ||
          'REOPEN',
        actionOnClosedReply:
          data.actionOnClosedReply?.action ||
          data.actionOnClosedReply ||
          'CREATE_NEW_TICKET'
      });
      getAssociatedAgents();
    }
  }, [data]);

  useEffect(() => {
    if (showModal) {
      setBody({
        ...data,
        actionOnCompletedReply:
          data.actionOnCompletedReply?.action ||
          data.actionOnCompletedReply ||
          'REOPEN',
        actionOnClosedReply:
          data.actionOnClosedReply?.action ||
          data.actionOnClosedReply ||
          'CREATE_NEW_TICKET'
      });
      setValidated(false);
    }
  }, [showModal]);

  useEffect(() => {
    const getAgents = async () => {
      let response = await api.agents.list();
      if (response.content) setAgents(response.content);
    };
    getAgents();
  }, []);

  const getAssociatedAgents = async () => {
    let response = await api.serviceGroupsAgents.list(data.id);
    setAssociatedAgents(response);
  };

  const associateAgent = async e => {
    e.preventDefault();
    let response = await api.serviceGroupsAgents.associate(
      data.id,
      selectedAgent
    );
    if (response.status === 204) {
      getAssociatedAgents();
      refreshData();
    } else {
      error({ description: response.description });
    }
  };

  const unassociateAgent = async (e, agentId) => {
    e.preventDefault();
    let response = await api.serviceGroupsAgents.unassociate(data.id, agentId);
    if (response.status === 204) {
      getAssociatedAgents();
      refreshData();
    } else {
      error({ description: response.description });
    }
  };

  const onSubmit = e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      if (parseInt(body.daysToAutoClose) <= 0) {
        showErrorNotification(
          'O número de dias para fechar o ticket deve ser maior que 0'
        );
        return;
      }

      handleSubmit({
        name: body.name,
        daysToAutoClose: body.daysToAutoClose,
        actionOnCompletedReply: body.actionOnCompletedReply,
        actionOnClosedReply: body.actionOnClosedReply,
        inactive: body.inactive
      });
      setValidated(false);
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Editar grupo de serviço</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Form.Group className="mb-3" controlId="editForm.Name">
            <Form.Label>
              Nome <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Nome"
              autoFocus
              value={body.name}
              onChange={e =>
                setBody(prevBody => ({ ...prevBody, name: e.target.value }))
              }
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um nome.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="editForm.daysToAutoClose">
            <Form.Label>
              Dias para fechar o ticket{' '}
              <OverlayTrigger
                overlay={
                  <Tooltip>
                    Após o ticket ser fechado, não será possível reabrir,
                    adicionar anexos, notas ou realizar qualquer ação sobre ele.
                  </Tooltip>
                }
              >
                <span>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </span>
              </OverlayTrigger>{' '}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              required
              type="number"
              placeholder="Dias para fechar o ticket"
              min={1}
              value={body.daysToAutoClose}
              onChange={e =>
                setBody(prevBody => ({
                  ...prevBody,
                  daysToAutoClose: e.target.value
                }))
              }
            />
            <Form.Text className="text-muted">
              Defina quantos dias corridos são necessários para fechar o ticket.
            </Form.Text>
          </Form.Group>

          {/** HANDLE ACTION ON COMPLETED LATE REPLY */}
          <h5 className="fw-semi-bold">Resposta tardia do solicitante</h5>
          <Form.Group
            className="mb-3"
            controlId="editForm.actionOnCompletedReply"
          >
            <Form.Label>
              Quando um solicitante responder a um ticket com status{' '}
              <Badge bg="dark" className="text-white">
                Concluído
              </Badge>{' '}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Check
              type="radio"
              label="Reabrir o ticket"
              value="REOPEN"
              checked={body.actionOnCompletedReply === 'REOPEN'}
              onChange={e =>
                setBody(prevBody => ({
                  ...prevBody,
                  actionOnCompletedReply: e.target.value
                }))
              }
            />
            <Form.Check
              type="radio"
              label="Criar novo ticket"
              value="CREATE_NEW_TICKET"
              checked={body.actionOnCompletedReply === 'CREATE_NEW_TICKET'}
              onChange={e =>
                setBody(prevBody => ({
                  ...prevBody,
                  actionOnCompletedReply: e.target.value
                }))
              }
            />
            <Form.Check
              type="radio"
              label="Enviar notificação de Ticket Encerrado"
              value="SEND_TEMPLATE_EMAIL"
              checked={body.actionOnCompletedReply === 'SEND_TEMPLATE_EMAIL'}
              onChange={e =>
                setBody(prevBody => ({
                  ...prevBody,
                  actionOnCompletedReply: e.target.value
                }))
              }
            />
          </Form.Group>

          {/** HANDLE LATE ACTION ON CLOSED REPLY */}
          <Form.Group className="mb-3" controlId="editForm.actionOnClosedReply">
            <Form.Label>
              Quando um solicitante responder a um ticket com status{' '}
              <Badge bg="secondary">Fechado</Badge>{' '}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Check
              type="radio"
              label="Criar novo ticket"
              value="CREATE_NEW_TICKET"
              checked={body.actionOnClosedReply === 'CREATE_NEW_TICKET'}
              onChange={e =>
                setBody(prevBody => ({
                  ...prevBody,
                  actionOnClosedReply: e.target.value
                }))
              }
            />
            <Form.Check
              type="radio"
              label="Enviar notificação de Ticket Encerrado"
              value="SEND_TEMPLATE_EMAIL"
              checked={body.actionOnClosedReply === 'SEND_TEMPLATE_EMAIL'}
              onChange={e =>
                setBody(prevBody => ({
                  ...prevBody,
                  actionOnClosedReply: e.target.value
                }))
              }
            />

            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Escolha uma opção!
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Check
              type="switch"
              id="status-switch"
              label={body.inactive ? 'Inativo' : 'Ativo'}
              checked={!body.inactive}
              onChange={() =>
                setBody(prevBody => ({
                  ...prevBody,
                  inactive: !prevBody.inactive
                }))
              }
            />
          </Form.Group>

          <Form.Label>Associe um agente</Form.Label>
          <Form.Group className="w-100 d-flex justify-content-between">
            <Form.Select
              aria-label="Agentes"
              className="flex-1 me-2"
              onChange={e => setSelectedAgent(e.target.value)}
            >
              <option>Selecione uma opção</option>
              {agents.map((item, i) => (
                <option value={item.id} key={i}>
                  {item.firstName}
                </option>
              ))}
            </Form.Select>
            <button className="btn btn-primary btn-sm" onClick={associateAgent}>
              + Adicionar
            </button>
          </Form.Group>
        </Form>

        <div className="table-responsive scrollbar">
          <table className="table fs-10 mb-0 mt-3">
            <thead className="bg-200 text-900">
              <tr>
                <th>Agentes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {associatedAgents.map((item, i) => (
                <tr key={i}>
                  <td data-value={item.id}>{item.firstName}</td>
                  <td>
                    <button
                      className="btn btn-link text-danger btn-sm"
                      onClick={e => unassociateAgent(e, item.id)}
                    >
                      x
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ServiceGroupEditModal;
